import React from 'react';
import PropTypes from 'prop-types';
import { FeatureGroup, GeoJSON, Map, Marker, Popup, TileLayer } from 'react-leaflet';
import axios from 'axios';
import toGeoJSON from '@mapbox/togeojson';
import Rx from 'rxjs';
import DOMPurify from 'dompurify';
import { connect } from 'react-redux';
import { RaisedButton } from 'material-ui';
import L from 'leaflet';
import Place from 'material-ui/svg-icons/maps/place';
import Terrain from 'material-ui/svg-icons/maps/terrain';
import styles from './Mapbox.css';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import ThemeDefault from '../../../styles/theme-default';
import MediasService from '../../../services/medias';
import withTranslation from '../../../hoc/withTranslation';
import { fetchApp } from '../../../store/apps/actions';
import { getType, getTypes } from '../../../store/types/actions';

export const MapStyle = {
    LIGHT: 'light-v9',
    OUTDOORS: 'outdoors-v10',
    STREETS: 'streets-v10',
    SATELLITE: 'satellite-v9',
    DARK: 'dark-v9',
};

const DEFAULT_IMAGE_WIDTH = 30;
const DEFAULT_IMAGE_HEIGHT = 30;

const createIconWithType = (type) => {
    if (type && type.picture) {
        const pictureUrl = type.picture;
        let pictureWidth = type.picture_width || DEFAULT_IMAGE_WIDTH;
        let pictureHeight = type.picture_height || DEFAULT_IMAGE_HEIGHT;
        const ratio = pictureWidth / pictureHeight;
        if (ratio < 1) {
            pictureHeight = DEFAULT_IMAGE_HEIGHT;
            pictureWidth = DEFAULT_IMAGE_WIDTH * ratio;
        } else {
            pictureWidth = DEFAULT_IMAGE_WIDTH;
            pictureHeight = DEFAULT_IMAGE_HEIGHT / ratio;
        }
        return new L.Icon({
            iconUrl: pictureUrl,
            iconRetinaUrl: pictureUrl,
            iconAnchor: [pictureWidth * 0.5, pictureHeight],
            popupAnchor: [0, -pictureHeight],
            iconSize: [pictureWidth, pictureHeight],
        });
    } else {
        return null;
    }
};

class Mapbox extends React.Component {
    token = 'pk.eyJ1IjoiZHJ1c3kiLCJhIjoiY2owYjBlNmhrMDAwdzJxcDloMnJybHd1NyJ9.H256nOJ0yauBFXMZJ9972A';

    static propTypes = {
        points: PropTypes.arrayOf(PropTypes.object),
        trails: PropTypes.arrayOf(PropTypes.object),
        quizzes: PropTypes.arrayOf(PropTypes.object),
        reports: PropTypes.arrayOf(PropTypes.object),
        onPointClicked: PropTypes.func,
        onTrailClicked: PropTypes.func,
        onQuizClicked: PropTypes.func,
        onReportClicked: PropTypes.func,
        onMapClicked: PropTypes.func,
        style: PropTypes.object, // Passed to the map as is
        mapStyle: PropTypes.string,
        canShowDetail: PropTypes.bool,
        scrollWheelZoom: PropTypes.bool,
        showPopups: PropTypes.bool,
        latlng: PropTypes.object, // If this property is defined, points & trails will be ignored
    };

    static defaultProps = {
        mapStyle: MapStyle.STREETS,
        canShowDetail: true,
        scrollWheelZoom: true,
        showPopups: true,
        points: [],
        trails: [],
        quizzes: [],
        reports: [],
    };

    state = {
        geojsons: [],
        cancelationTokens: [],
    };

    zoom = 10;
    mapHadUserInteraction = false;

    handlePointClick = (point) => {
        if (this.props.onPointClicked) {
            this.props.onPointClicked(point);
        }
    };

    handleQuizClick = (quiz) => {
        if (this.props.onQuizClicked) {
            this.props.onQuizClicked(quiz);
        }
    };

    handleReportClick = (report) => {
        if (this.props.onReportClicked) {
            this.props.onReportClicked(report);
        }
    };

    componentDidMount() {
        const leafletMap = this.leafletMap.leafletElement;
        leafletMap.on('dragstart', () => {
            this.mapHadUserInteraction = true;
        });
        leafletMap.on('click', () => {
            this.mapHadUserInteraction = true;
        });
        leafletMap.on('dblclick', () => {
            this.mapHadUserInteraction = true;
        });
        leafletMap.on('zoomend', () => {
            this.zoom = leafletMap.getZoom();
        });
        this.fetchGeojson(this.props.trails);
        this.fetchGpx(this.props.trails);
        this.fetchKml(this.props.trails);

        this.subject = new Rx.Subject();
        this.subcription = this.subject.debounceTime(1000).subscribe(() => {
            const bounds = this.computeBounds();

            if (bounds) {
                this.leafletMap.leafletElement.fitBounds(bounds);
            }
        });
    }

    componentWillUnmount() {
        this.subcription.unsubscribe();
        this.subject.complete();
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.match !== nextProps.match) {
            this.fetchApplication(nextProps.match.params.appId);
        }
        if (this.props.trails !== nextProps.trails) {
            this.state.cancelationTokens.forEach((token) => {
                token.cancel();
            });
            this.setState({
                geojsons: [],
                cancelationTokens: [],
            });
            this.forceUpdate();
            this.fetchGeojson(nextProps.trails);
            this.fetchGpx(nextProps.trails);
            this.fetchKml(nextProps.trails);
        }
        if (this.props.latlng !== nextProps.latlng) {
            this.forceUpdate();
        }
        if (this.props.points !== nextProps.points) {
            this.forceUpdate();
        }
        if (this.props.quizzes !== nextProps.quizzes) {
            this.forceUpdate();
        }
        if (this.props.reports !== nextProps.reports) {
            this.forceUpdate();
        }
        if (this.props.mapStyle !== nextProps.mapStyle) {
            this.forceUpdate();
        }
        if (this.props.style !== nextProps.style) {
            setTimeout(() => {
                this.leafletMap.leafletElement.invalidateSize(false);
            }, 100); // Adjust timeout to tab transition
        }
    }

    // Fetch

    fetchApplication(appId = null) {
        if (this.props.match) {
            let id = appId || this.props.match.params.appId;
            if (id) {
                this.props.fetchApp(id);
            }
        }
    }

    shouldComponentUpdate() {
        return false;
    }

    // Utils

    componentDidUpdate() {
        this.subject.next();
    }

    computeCenter() {
        let center = [this.props.app.center_latitude, this.props.app.center_longitude];
        if (this.leafletMap) {
            center = this.leafletMap.leafletElement.getCenter();
        }
        if (this.props.latlng && this.props.latlng.shouldCenter) {
            center = [this.props.latlng.lat, this.props.latlng.lng];
        }
        return center;
    }

    // Handlers

    addGeojson = (geojson) => {
        const others = this.state.geojsons.filter((elt) => {
            return elt.trail.id !== geojson.trail.id;
        });
        this.setState({
            geojsons: [
                ...others,
                geojson,
            ],
        });
        this.forceUpdate();
    };

    computeBounds() {
        if (!this.props.latlng && !this.mapHadUserInteraction) {
            if (this.layerGroup && this.layerGroup.leafletElement.getLayers().length > 0) {
                return this.layerGroup.leafletElement.getBounds();
            }
        }
        return undefined;
    }

    handleTrailClick = (trail) => {
        if (this.props.onTrailClicked) {
            this.props.onTrailClicked(trail);
        }
    };

    // Fetch

    fetchGpx(trails) {
        trails.forEach((trail) => {
            if (trail.gpx) {
                const source = axios.CancelToken.source();

                this.setState({
                    cancelationTokens: [
                        ...this.state.cancelationTokens,
                        source,
                    ],
                });

                axios
                    .get(trail.gpx, { cancelToken: source.token })
                    .then((response) => {
                        const gpx = new DOMParser().parseFromString(response.data, 'text/xml');
                        const geojson = toGeoJSON.gpx(gpx);

                        this.addGeojson({
                            data: geojson,
                            trail: trail,
                            style: styles.gpx,
                        });
                    })
                    .catch(function (error) {
                        console.log(error);
                    });
            }
        });
    }

    fetchKml(trails) {
        trails.forEach((trail) => {
            if (trail.kml) {
                const source = axios.CancelToken.source();

                this.setState({
                    cancelationTokens: [
                        ...this.state.cancelationTokens,
                        source,
                    ],
                });

                axios
                    .get(trail.kml, { cancelToken: source.token })
                    .then((response) => {
                        const kml = new DOMParser().parseFromString(response.data, 'text/xml');
                        const geojson = toGeoJSON.kml(kml);
                        this.addGeojson({
                            data: geojson,
                            trail: trail,
                            style: styles.kml,
                        });
                    })
                    .catch(function (error) {
                        console.log(error);
                    });
            }
        });
    }

    fetchGeojson(trails) {
        trails.forEach((trail) => {
            if (trail.geojson) {
                const source = axios.CancelToken.source();

                this.setState({
                    cancelationTokens: [
                        ...this.state.cancelationTokens,
                        source,
                    ],
                });

                axios
                    .get(trail.geojson, { cancelToken: source.token })
                    .then((response) => {
                        this.addGeojson({
                            data: response.data,
                            trail: trail,
                            style: styles.geojson,
                        });
                    })
                    .catch(function (error) {
                        console.log(error);
                    });
            }
        });
    }

    // Render

    renderPointsMarkers() {

        let markers;
        if (this.props.latlng) {
            let type = null;
            if (this.props.currentPoint && this.props.currentPoint.types_id) {
                if (this.props.currentPoint.types_id.length > 0) {
                    const typeId = this.props.currentPoint.types_id[0];
                    type = this.props.types.find(type => type.id === typeId);
                }
            }

            let pictureIcon = null;

            if (type !== null) {
                pictureIcon = createIconWithType(type);
            }
            if (pictureIcon) {
                markers = (
                    <Marker position={this.props.latlng} icon={pictureIcon} />
                );
            } else {
                markers = (
                    <Marker position={this.props.latlng} />
                );
            }
        } else {
            markers = this.props.points
                .filter((point) => {
                    return point.latitude && point.longitude;
                })
                .map((point) => {
                    let position = [point.latitude, point.longitude];
                    let media = null;

                    if (point.medias) {
                        let images = MediasService.sortedMediasForShortMime(point.medias,
                            MediasService.IMAGES,
                        );

                        if (images.length > 0) {
                            media = images[0];
                        }
                    }

                    let pictureIcon = null;
                    if (point.types_id && point.types_id.length > 0 && this.props.types) {
                        const typeId = point.types_id[0];
                        const type = this.props.types.find(type => type.id === typeId);
                        if (type) {
                            pictureIcon = createIconWithType(type);
                        }
                    }

                    const markerContent = <Popup autoPan={true}>
                        <MuiThemeProvider muiTheme={ThemeDefault}>
                            <div>
                                <b>{point.name}</b>
                                <br />
                                <div style={{
                                    maxHeight: '400px',
                                    overflow: 'auto',
                                }}>
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(point.description),
                                        }}
                                    />
                                    <br />
                                    {media &&
                                        <img
                                            alt={media.name || ''}
                                            style={styles.marker.popup.img}
                                            src={media.link}
                                        />
                                    }
                                </div>
                                {this.props.canShowDetail &&
                                    <div>
                                        <br />
                                        <RaisedButton
                                            fullWidth={true}
                                            label={this.props.t('generic.detail')}
                                            labelPosition="after"
                                            primary={true}
                                            icon={<Place />}
                                            onClick={() => {
                                                this.handlePointClick(point);
                                            }}
                                        />
                                    </div>
                                }
                            </div>
                        </MuiThemeProvider>
                    </Popup>;

                    if (pictureIcon) {
                        return (
                            <Marker key={point.id} position={position} icon={pictureIcon}>
                                {this.props.showPopups && markerContent}
                            </Marker>
                        );
                    } else {
                        return (
                            <Marker key={point.id} position={position}>
                                {this.props.showPopups && markerContent}
                            </Marker>
                        );
                    }
                });
        }

        return (
            <div>
                {markers}
            </div>
        );
    }

    renderReportsMarkers() {
        let markers;

        if (!this.props.latlng) {
            markers = this.props.reports
                .filter((report) => {
                    return report.latitude && report.longitude;
                })
                .map((report) => {
                    let position = [report.latitude, report.longitude];
                    let media = null;

                    if (report.medias) {
                        let images = MediasService.sortedMediasForShortMime(report.medias,
                            MediasService.IMAGES,
                        );

                        if (images.length > 0) {
                            media = images[0];
                        }
                    }

                    return (
                        <Marker key={report.id} position={position}>
                            {this.props.showPopups &&
                                <Popup autoPan={true}>
                                    <MuiThemeProvider muiTheme={ThemeDefault}>
                                        <div>
                                            <b>{report.title}</b>
                                            <br />
                                            <div dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(report.description),
                                            }} />
                                            <br />
                                            {report.medias && report.medias.length > 0 &&
                                                <img
                                                    alt={media.name || ''}
                                                    style={styles.marker.popup.img}
                                                    src={media.link}
                                                />
                                            }
                                            {this.props.canShowDetail &&
                                                <div>
                                                    <br />
                                                    <RaisedButton
                                                        fullWidth={true}
                                                        label={this.props.t('generic.detail')}
                                                        labelPosition="after"
                                                        primary={true}
                                                        icon={<Place />}
                                                        onClick={() => {
                                                            this.handleReportClick(report);
                                                        }}
                                                    />
                                                </div>
                                            }
                                        </div>
                                    </MuiThemeProvider>
                                </Popup>
                            }
                        </Marker>
                    );
                });
        }

        return (
            <div>
                {markers}
            </div>
        );
    }

    renderQuizzesMarkers() {
        let markers;
        if (!this.props.latlng) {
            markers = this.props.quizzes
                .filter((quiz) => {
                    return quiz.latitude && quiz.longitude;
                })
                .map((quiz) => {
                    let position = [quiz.latitude, quiz.longitude];
                    return (
                        <Marker key={quiz.id} position={position}>
                            {this.props.showPopups &&
                                <Popup autoPan={true}>
                                    <MuiThemeProvider muiTheme={ThemeDefault}>
                                        <div>
                                            <b>{quiz.name}</b>
                                            {this.props.canShowDetail &&
                                                <div>
                                                    <br />
                                                    <RaisedButton
                                                        fullWidth={true}
                                                        label={this.props.t('generic.detail')}
                                                        labelPosition="after"
                                                        primary={true}
                                                        icon={<Place />}
                                                        onClick={() => {
                                                            this.handleQuizClick(quiz);
                                                        }}
                                                    />
                                                </div>
                                            }
                                        </div>
                                    </MuiThemeProvider>
                                </Popup>
                            }
                        </Marker>
                    );
                });
        }
        return (
            <div>
                {markers}
            </div>
        );
    }

    renderGeojson() {
        let geojsons = this.state.geojsons.map((geojson) => {
            const trail = geojson.trail;
            let media = null;

            if (trail.medias) {
                let images = MediasService.sortedMediasForShortMime(trail.medias,
                    MediasService.IMAGES,
                );

                if (images.length > 0) {
                    media = images[0];
                }
            }
            return (
                <GeoJSON
                    key={trail.id}
                    data={geojson.data}
                    style={geojson.style}
                >
                    {this.props.showPopups &&
                        <Popup>
                            <MuiThemeProvider muiTheme={ThemeDefault}>
                                <div>
                                    <b>{trail.name}</b><br />
                                    <div dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(trail.description),
                                    }} />
                                    <br />

                                    {trail.medias && trail.medias.length > 0 &&
                                        <img
                                            alt={media.name || ''}
                                            style={styles.marker.popup.img}
                                            src={media.link}
                                        />}
                                    {this.props.canShowDetail &&
                                        <div>
                                            <br />
                                            <RaisedButton
                                                fullWidth={true}
                                                label={this.props.t('generic.detail')}
                                                labelPosition="after"
                                                primary={true}
                                                icon={<Terrain />}
                                                onClick={() => {
                                                    this.handleTrailClick(trail);
                                                }}
                                            />
                                        </div>
                                    }
                                </div>
                            </MuiThemeProvider>
                        </Popup>
                    }
                </GeoJSON>
            );
        });

        return (
            <div>
                {geojsons}
            </div>
        );
    }

    render() {
        return (
            <Map
                ref={(map) => {
                    this.leafletMap = map;
                }}
                onClick={this.props.onMapClicked}
                scrollWheelZoom={this.props.scrollWheelZoom}
                style={this.props.style}
                center={this.computeCenter()}
                zoom={this.zoom}
                bounds={this.computeBounds()}
                boundsOptions={{ padding: [75, 75] }}
            >
                <TileLayer
                    detectRetina={true}
                    url={`https://api.mapbox.com/styles/v1/mapbox/${this.props.mapStyle}/tiles/256/{z}/{x}/{y}{r}?access_token=${this.token}`}
                    attribution="Open Explore"
                />
                <FeatureGroup ref={(group) => {this.layerGroup = group;}}>
                    {this.renderPointsMarkers()}
                    {this.renderQuizzesMarkers()}
                    {this.renderReportsMarkers()}
                    {this.renderGeojson()}
                </FeatureGroup>
                {this.props.children}
            </Map>
        );
    }
}

function mapStateToProps(state, ownProps) {
    return {
        loading: state.apps.pending,
        sending: state.apps.sending,
        appError: state.apps.error,
        app: state.apps.currentApplication,
        types: state.types.content,
        currentPoint: state.points.currentPoint,
    };
}

export default connect(mapStateToProps, {
    fetchApp,
    getTypes,
    getType,
})(withTranslation(Mapbox));



// WEBPACK FOOTER //
// src/components/openexplore/Mapbox/Mapbox.js